import type { BoulevardSales, PayoutPayment, PurchasesReport, SalesReport, UnfinishedBookingsReport } from "@vaulted/shared";
import { PAYMENT_METHOD_LABELS, formatCents } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Navigate, useParams } from "react-router";
import { del, errorMessage, get, post } from "../api";
import { ExportButtons } from "../components/ExportButtons";
import { type ReportTable, dollars } from "../components/exportReport";
import { Alert, Button, Checkbox, Dialog, Loading, PageHeader, Panel, PanelHeader, Tabs, TextAreaField } from "../components/ui";
import { useSession } from "../session";
import { addDays, dayLabel, todayIn } from "./Calendar";
import { time12 } from "../components/AppointmentDialog";
import { PaymentsReport } from "./Payments";
import { PayeeRows, payeeOfPayment, payoutTables, periodRange } from "./Payouts";
import { MembershipsReportView } from "./MembershipsReport";
import { PurchasesReportView } from "./Purchases";

const TABS = [
  { to: "/reports/sales", label: "Sales" },
  { to: "/reports/payments", label: "Payments" },
  { to: "/reports/purchases", label: "Purchases" },
  { to: "/reports/memberships", label: "Memberships" },
  { to: "/reports/payouts", label: "Payouts" },
  { to: "/reports/unfinished", label: "Unfinished bookings" },
];

/** Reports: standard reports as tabs (custom reports can come later). */
export function ReportsPage() {
  const { tab } = useParams();
  if (!tab || !TABS.some((t) => t.to.endsWith(`/${tab}`))) return <Navigate to="/reports/sales" replace />;
  return (
    <div>
      <PageHeader title="Reports" description="Money is counted on the day it was paid, in the clinic's time zone." />
      <Tabs items={TABS} />
      {tab === "unfinished" ? <UnfinishedTab /> : tab === "sales" ? <SalesTab /> : tab === "purchases" ? <PurchasesTab /> : tab === "memberships" ? <MembershipsReportView /> : tab === "payouts" ? <PayoutsTab /> : <PaymentsReport />}
    </div>
  );
}

// ── Reporting periods

type PeriodKey = "today" | "yesterday" | "this_week" | "last_week" | "this_month" | "last_month" | "this_quarter" | "ytd" | "last_year" | "custom";
const PERIOD_LABELS: Record<PeriodKey, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  ytd: "Year to date",
  last_year: "Last year",
  custom: "Custom dates",
};

/** A standard period as dates (weeks start Monday). */
export function periodDates(key: Exclude<PeriodKey, "custom">, today: string): { from: string; to: string } {
  const [y, m] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  const pad = (n: number) => String(n).padStart(2, "0");
  const monday = addDays(today, -((new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7));
  const monthStart = (yy: number, mm: number) => `${yy}-${pad(mm)}-01`;
  const monthEnd = (yy: number, mm: number) => addDays(mm === 12 ? `${yy + 1}-01-01` : monthStart(yy, mm + 1), -1);
  switch (key) {
    case "today":
      return { from: today, to: today };
    case "yesterday":
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case "this_week":
      return { from: monday, to: today };
    case "last_week":
      return { from: addDays(monday, -7), to: addDays(monday, -1) };
    case "this_month":
      return { from: monthStart(y, m), to: today };
    case "last_month":
      return m === 1 ? { from: monthStart(y - 1, 12), to: monthEnd(y - 1, 12) } : { from: monthStart(y, m - 1), to: monthEnd(y, m - 1) };
    case "this_quarter":
      return { from: monthStart(y, Math.floor((m - 1) / 3) * 3 + 1), to: today };
    case "ytd":
      return { from: `${y}-01-01`, to: today };
    case "last_year":
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  }
}

function PeriodPicker({ value, onChange, children }: { value: { key: PeriodKey; from: string; to: string }; onChange: (v: { key: PeriodKey; from: string; to: string }) => void; children?: ReactNode }) {
  const { me } = useSession();
  const today = todayIn(me?.clinic.timezone);
  const input = "rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-sm";
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-sm font-semibold text-ink-soft">
        Period
        <select
          className={input}
          value={value.key}
          onChange={(e) => {
            const key = e.target.value as PeriodKey;
            onChange(key === "custom" ? { ...value, key } : { key, ...periodDates(key, today) });
          }}
        >
          {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((k) => (
            <option key={k} value={k}>
              {PERIOD_LABELS[k]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold text-ink-soft">
        From
        <input type="date" className={input} value={value.from} max={value.to} onChange={(e) => e.target.value && onChange({ key: "custom", from: e.target.value, to: value.to })} />
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold text-ink-soft">
        To
        <input type="date" className={input} value={value.to} min={value.from} onChange={(e) => e.target.value && onChange({ key: "custom", from: value.from, to: e.target.value })} />
      </label>
      {children && <div className="ml-auto flex gap-2">{children}</div>}
    </div>
  );
}

// ── Sales

function SalesTab() {
  const { me } = useSession();
  const [period, setPeriod] = useState<{ key: PeriodKey; from: string; to: string }>(() => ({ key: "this_month", ...periodDates("this_month", todayIn(me?.clinic.timezone)) }));
  const [withBoulevard, setWithBoulevard] = useState(false);
  const report = useQuery({
    queryKey: ["report-sales", period.from, period.to, withBoulevard],
    queryFn: () => get<SalesReport>(`/api/reports/sales?from=${period.from}&to=${period.to}${withBoulevard ? "&boulevard=1" : ""}`),
  });
  const r = report.data;
  const range = period.from === period.to ? dayLabel(period.from, { month: "short", day: "numeric", year: "numeric" }) : `${dayLabel(period.from, { month: "short", day: "numeric", year: "numeric" })} – ${dayLabel(period.to, { month: "short", day: "numeric", year: "numeric" })}`;
  const tables = (): ReportTable[] => {
    if (!r) return [];
    const s = r.summary;
    return [
      {
        title: "Summary",
        columns: ["", "Amount"],
        rows: [
          ["Period", `${r.from} to ${r.to}`],
          ["Visits checked out", s.visits],
          ["Gross sales", dollars(s.grossCents)],
          ["Covered by vouchers (prepaid)", dollars(-s.coveredCents)],
          ["Discounts and member pricing", dollars(-s.discountsCents)],
          ["Net sales", dollars(s.netCents)],
          ["Tips", dollars(s.tipsCents)],
          ["Deposits applied", dollars(s.depositsAppliedCents)],
          ["Memberships and packages sold", dollars(s.planSalesCents)],
          ["No-show and late-cancel fees", dollars(s.feesCents)],
          ["Refunds given as account credit", dollars(-s.refundsToCreditCents)],
          ["Paid with account credit", dollars(s.creditUsedCents)],
          ...s.collected.map((c): [string, number] => [`Collected: ${PAYMENT_METHOD_LABELS[c.method]}`, dollars(c.amountCents)]),
          ["Refunds", dollars(-s.refundsCents)],
          ["Collected, after refunds", dollars(s.collectedCents)],
        ],
      },
      { title: "By item", columns: ["Category", "Item", "Quantity", "Amount"], rows: r.items.map((i) => [i.category, i.name, i.quantity, dollars(i.amountCents)]) },
      { title: "By nurse", columns: ["Nurse", "Visits", "Net sales", "Tips"], rows: r.nurses.map((n) => [n.name, n.visits, dollars(n.netCents), dollars(n.tipsCents)]) },
      { title: "By day", columns: ["Date", "Visits", "Net sales", "Tips", "Collected"], rows: r.days.map((d) => [d.date, d.visits, dollars(d.netCents), dollars(d.tipsCents), dollars(d.collectedCents)]) },
      ...(r.boulevard
        ? [
            {
              title: "Combined (Vaulted + Boulevard)",
              columns: ["", "Vaulted", "Boulevard", "Total"],
              rows: (
                [
                  ["Visits", s.visits, r.boulevard.visits, s.visits + r.boulevard.visits],
                  ["Gross sales", s.grossCents, r.boulevard.grossCents, null],
                  ["Net sales", s.netCents, r.boulevard.netCents, null],
                  ["Tips", s.tipsCents, r.boulevard.tipsCents, null],
                  ["Collected", s.collectedCents, r.boulevard.collectedCents, null],
                ] as const
              ).map(([label, v, b, n]) => (n !== null ? [label, v, b, n] : [label, dollars(v), dollars(b), dollars(v + b)])),
            },
            {
              title: "Boulevard summary",
              columns: ["", "Amount"],
              rows: [
                ["Visits checked out in Boulevard", r.boulevard.visits],
                ["Gross sales", dollars(r.boulevard.grossCents)],
                ["Discounts", dollars(-r.boulevard.discountsCents)],
                ["Net sales", dollars(r.boulevard.netCents)],
                ["Tips", dollars(r.boulevard.tipsCents)],
                ["Tax and fees", dollars(r.boulevard.taxAndFeesCents)],
                ["Paid with vouchers and gift cards (prepaid)", dollars(r.boulevard.prepaidCents)],
                ["Refunds", dollars(-r.boulevard.refundsCents)],
                ...r.boulevard.collected.map((c): [string, number] => [`Collected: ${c.label}`, dollars(c.amountCents)]),
                ["Collected, total", dollars(r.boulevard.collectedCents)],
              ] as (string | number)[][],
            },
            { title: "Boulevard by item", columns: ["Category", "Item", "Quantity", "Amount"], rows: r.boulevard.items.map((i) => [i.category, i.name, i.quantity, dollars(i.amountCents)]) },
            { title: "Boulevard by day", columns: ["Date", "Visits", "Net sales", "Tips", "Collected"], rows: r.boulevard.days.map((d) => [d.date, d.visits, dollars(d.netCents), dollars(d.tipsCents), dollars(d.collectedCents)]) },
          ]
        : []),
    ];
  };

  return (
    <div>
      <PeriodPicker value={period} onChange={setPeriod}>
        {r && <ExportButtons name={`Sales ${period.from} to ${period.to}`} tables={tables} />}
      </PeriodPicker>
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <p className="text-sm text-muted">{range}</p>
        <Checkbox label="Include Boulevard" description="Adds checkouts done in Boulevard: the totals at the top become both together, with each one's own breakdown below." checked={withBoulevard} onChange={setWithBoulevard} />
      </div>
      {report.isLoading ? (
        <Loading />
      ) : report.error || !r ? (
        <Alert>{errorMessage(report.error)}</Alert>
      ) : (
        <div className="flex flex-col gap-4">
          {r.boulevard ? (
            // With Boulevard: the totals are both together, each split out underneath.
            <>
              <p className="-mb-2 text-sm font-semibold text-ink-soft">Vaulted + Boulevard</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {(
                  [
                    ["Net sales", r.summary.netCents, r.boulevard.netCents, `${r.summary.visits + r.boulevard.visits} visits`],
                    ["Gross sales", r.summary.grossCents, r.boulevard.grossCents, null],
                    ["Tips", r.summary.tipsCents, r.boulevard.tipsCents, null],
                    ["Collected", r.summary.collectedCents, r.boulevard.collectedCents, null],
                  ] as const
                ).map(([label, v, b, extra]) => (
                  <Stat key={label} label={label} value={formatCents(v + b)} sub={`${extra ? `${extra} · ` : ""}${formatCents(v)} Vaulted · ${formatCents(b)} Boulevard`} />
                ))}
              </div>
            </>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Net sales" value={formatCents(r.summary.netCents)} sub={`${r.summary.visits} visit${r.summary.visits === 1 ? "" : "s"} checked out`} />
              <Stat label="Gross sales" value={formatCents(r.summary.grossCents)} sub={r.summary.discountsCents ? `${formatCents(r.summary.discountsCents)} in discounts` : "No discounts"} />
              <Stat label="Tips" value={formatCents(r.summary.tipsCents)} />
              <Stat label="Collected" value={formatCents(r.summary.collectedCents)} sub={r.summary.refundsCents ? `After ${formatCents(r.summary.refundsCents)} refunded` : undefined} />
            </div>
          )}
          <Panel>
            <PanelHeader title={r.boulevard ? "Vaulted summary" : "Summary"} />
            <table className="table-base">
              <tbody>
                <Row label="Gross sales" cents={r.summary.grossCents} />
                {r.summary.coveredCents > 0 && <Row label="Covered by membership and package vouchers (prepaid)" cents={-r.summary.coveredCents} />}
                <Row label="Discounts and member pricing" cents={-r.summary.discountsCents} />
                <Row label="Net sales" cents={r.summary.netCents} strong />
                <Row label="Tips" cents={r.summary.tipsCents} />
                <Row label="Deposits applied (paid earlier)" cents={r.summary.depositsAppliedCents} />
                <Row label="No-show and late-cancel fees" cents={r.summary.feesCents} />
                <Row label="Memberships and packages sold" cents={r.summary.planSalesCents} />
                {r.summary.refundsToCreditCents > 0 && <Row label="Refunds given as account credit" cents={-r.summary.refundsToCreditCents} />}
                {r.summary.creditUsedCents > 0 && <Row label="Paid with account credit (not collected)" cents={r.summary.creditUsedCents} />}
                {r.summary.collected.map((c) => (
                  <Row key={c.method} label={`Collected: ${PAYMENT_METHOD_LABELS[c.method]} (${c.count})`} cents={c.amountCents} />
                ))}
                <Row label="Refunds" cents={-r.summary.refundsCents} />
                <Row label="Collected, after refunds" cents={r.summary.collectedCents} strong />
              </tbody>
            </table>
          </Panel>
          <Table title="By item" columns={["Category", "Item", "Qty", "Amount"]} right={2} empty="No sales in this period." rows={r.items.map((i) => [i.category, i.name, String(i.quantity), formatCents(i.amountCents)])} />
          <Table title="By nurse" columns={["Nurse", "Visits", "Net sales", "Tips"]} right={3} empty="No visits paid in this period." rows={r.nurses.map((n) => [n.name, String(n.visits), formatCents(n.netCents), formatCents(n.tipsCents)])} />
          <Table
            title="By day"
            columns={["Date", "Visits", "Net sales", "Tips", "Collected"]}
            right={4}
            empty="Nothing in this period."
            rows={r.days.map((d) => [dayLabel(d.date, { weekday: "short", month: "short", day: "numeric" }), String(d.visits), formatCents(d.netCents), formatCents(d.tipsCents), formatCents(d.collectedCents)])}
          />
          {r.boulevard && <BoulevardSalesSection b={r.boulevard} />}
        </div>
      )}
    </div>
  );
}

/** Checkouts done in Boulevard (their receipts), on their own: never added into Vaulted's figures. */
function BoulevardSalesSection({ b }: { b: BoulevardSales }) {
  return (
    <>
      <h2 className="mt-4 font-display text-xl">Boulevard</h2>
      <p className="-mt-2 text-sm text-muted">Checkouts done in Boulevard, by the day they closed there. Separate from the figures above.</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Net sales" value={formatCents(b.netCents)} sub={`${b.visits} visit${b.visits === 1 ? "" : "s"}`} />
        <Stat label="Gross sales" value={formatCents(b.grossCents)} sub={b.discountsCents ? `${formatCents(b.discountsCents)} in discounts` : "No discounts"} />
        <Stat label="Tips" value={formatCents(b.tipsCents)} />
        <Stat label="Collected" value={formatCents(b.collectedCents)} sub={b.refundsCents ? `${formatCents(b.refundsCents)} refunded` : undefined} />
      </div>
      <Panel>
        <PanelHeader title="Boulevard summary" />
        <table className="table-base">
          <tbody>
            <Row label="Gross sales" cents={b.grossCents} />
            <Row label="Discounts" cents={-b.discountsCents} />
            <Row label="Net sales" cents={b.netCents} strong />
            <Row label="Tips" cents={b.tipsCents} />
            <Row label="Tax and fees" cents={b.taxAndFeesCents} />
            <Row label="Paid with vouchers and gift cards (prepaid)" cents={b.prepaidCents} />
            <Row label="Refunds" cents={-b.refundsCents} />
            {b.collected.map((c) => (
              <Row key={c.label} label={`Collected: ${c.label} (${c.count})`} cents={c.amountCents} />
            ))}
            <Row label="Collected, total" cents={b.collectedCents} strong />
          </tbody>
        </table>
      </Panel>
      <Table title="Boulevard by item" columns={["Category", "Item", "Qty", "Amount"]} right={2} empty="No Boulevard checkouts in this period." rows={b.items.map((i) => [i.category, i.name, String(i.quantity), formatCents(i.amountCents)])} />
      <Table
        title="Boulevard by day"
        columns={["Date", "Visits", "Net sales", "Tips", "Collected"]}
            right={4}
        empty="Nothing in this period."
        rows={b.days.map((d) => [dayLabel(d.date, { weekday: "short", month: "short", day: "numeric" }), String(d.visits), formatCents(d.netCents), formatCents(d.tipsCents), formatCents(d.collectedCents)])}
      />
    </>
  );
}

export function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Panel className="p-4">
      <p className="text-sm text-muted">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted">{sub}</p>}
    </Panel>
  );
}

function Row({ label, cents, strong }: { label: string; cents: number; strong?: boolean }) {
  cents ||= 0; // no "−$0.00"
  return (
    <tr className={strong ? "font-semibold" : undefined}>
      <td>{label}</td>
      <td className="text-right tabular-nums">{cents < 0 ? `−${formatCents(-cents)}` : formatCents(cents)}</td>
    </tr>
  );
}

/** `right`: how many of the last columns are numbers (lined up on the right, heading and all). */
export function Table({ title, columns, rows, empty, right = 1 }: { title: string; columns: string[]; rows: string[][]; empty: string; right?: number }) {
  return (
    <Panel className="overflow-x-auto">
      <PanelHeader title={title} />
      {rows.length ? (
        <table className="table-base">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={c} className={i > 0 && i >= columns.length - right ? "text-right" : undefined}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((v, j) => (
                  <td key={j} className={j > 0 && j >= columns.length - right ? "text-right tabular-nums" : undefined}>
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="px-5 pb-5 text-sm text-muted">{empty}</p>
      )}
    </Panel>
  );
}

// ── Purchases

function PurchasesTab() {
  const { me } = useSession();
  const [period, setPeriod] = useState<{ key: PeriodKey; from: string; to: string }>(() => ({ key: "ytd", ...periodDates("ytd", todayIn(me?.clinic.timezone)) }));
  const report = useQuery({ queryKey: ["report-purchases", period.from, period.to], queryFn: () => get<PurchasesReport>(`/api/reports/purchases?from=${period.from}&to=${period.to}`) });
  return (
    <div>
      <PeriodPicker value={period} onChange={setPeriod} />
      {report.error && <Alert>{errorMessage(report.error)}</Alert>}
      {report.isLoading ? <Loading /> : report.data && <PurchasesReportView r={report.data} />}
    </div>
  );
}

// ── Payouts marked paid

function PayoutsTab() {
  const { me, can } = useSession();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<{ key: PeriodKey; from: string; to: string }>(() => ({ key: "ytd", ...periodDates("ytd", todayIn(me?.clinic.timezone)) }));
  const report = useQuery({ queryKey: ["payout-payments", period.from, period.to], queryFn: () => get<PayoutPayment[]>(`/api/payouts/payments?from=${period.from}&to=${period.to}`) });
  const [open, setOpen] = useState<string | null>(null);
  const undo = useMutation({
    mutationFn: (id: string) => del(`/api/payouts/payments/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["payout-payments"] });
      await qc.invalidateQueries({ queryKey: ["payouts"] });
    },
  });
  const all = report.data ?? [];
  // Declined payouts (not taken, e.g. reinvested) are listed on their own and left out of the paid total.
  const rows = all.filter((p) => !p.declined);
  const declined = all.filter((p) => p.declined);
  const total = rows.reduce((t, p) => t + p.amountCents, 0);
  const declinedTotal = declined.reduce((t, p) => t + p.amountCents, 0);
  const row = (p: PayoutPayment) => (
    <PayeeRows
      key={p.id}
      payee={payeeOfPayment(p)}
      open={open === p.id}
      onToggle={() => setOpen(open === p.id ? null : p.id)}
      action={
        <span className="flex items-center justify-end gap-3 text-sm text-muted">
          <span>
            {periodRange(p.periodStart, p.periodEnd)}
            <br />
            {p.declined ? "Declined" : "Paid"} {dayLabel(p.paidAt.slice(0, 10), { month: "short", day: "numeric", year: "numeric" })}
            {p.paidBy ? ` by ${p.paidBy}` : ""}
            {p.declined && p.note ? (
              <>
                <br />
                {p.note}
              </>
            ) : null}
          </span>
          {can("payouts.manage") && (
            <Button
              variant="quiet"
              busy={undo.isPending && undo.variables === p.id}
              onClick={() => window.confirm(`Undo marking ${p.name} ${p.declined ? "declined" : "paid"} for ${periodRange(p.periodStart, p.periodEnd)}? It goes back on the Payouts page, recalculated.`) && undo.mutate(p.id)}
            >
              Undo
            </Button>
          )}
        </span>
      }
    />
  );
  return (
    <div>
      <PeriodPicker value={period} onChange={setPeriod}>
        {rows.length > 0 && <ExportButtons name={`Payouts paid ${period.from} to ${period.to}`} tables={() => payoutTables(rows.map((p) => ({ period: `${p.periodStart} to ${p.periodEnd}`, payee: payeeOfPayment(p) })))} />}
      </PeriodPicker>
      <p className="mb-4 text-sm text-muted">By the day each was marked paid. What's still owed is on the Payouts page.</p>
      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <Alert>{errorMessage(report.error)}</Alert>
      ) : (
        <Panel>
          <PanelHeader title="Paid" action={<span className="font-semibold tabular-nums">{formatCents(total)}</span>} />
          {undo.error && (
            <div className="px-5 pt-3">
              <Alert>{errorMessage(undo.error)}</Alert>
            </div>
          )}
          <table className="table-base">
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td className="text-muted">Nothing marked paid in this period.</td>
                </tr>
              )}
              {rows.map(row)}
            </tbody>
          </table>
        </Panel>
      )}
      {declined.length > 0 && (
        <Panel className="mt-6">
          <PanelHeader title="Declined" description="Not paid out (e.g. reinvested in the company); settled for their periods." action={<span className="font-semibold tabular-nums text-muted">{formatCents(declinedTotal)}</span>} />
          <table className="table-base">
            <tbody>{declined.map(row)}</tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// ── Unfinished bookings

/** Where people stop on the booking page, and who signed in but didn't book (to follow up). */
function UnfinishedTab() {
  const { me } = useSession();
  const [period, setPeriod] = useState<{ key: PeriodKey; from: string; to: string }>(() => ({ key: "this_week", ...periodDates("this_week", todayIn(me?.clinic.timezone)) }));
  const report = useQuery({ queryKey: ["report-unfinished", period.from, period.to], queryFn: () => get<UnfinishedBookingsReport>(`/api/reports/unfinished-bookings?from=${period.from}&to=${period.to}`) });
  const [texting, setTexting] = useState<UnfinishedBookingsReport["unfinished"][number] | null>(null);
  const r = report.data;
  const top = Math.max(1, ...(r?.funnel.map((f) => Math.max(f.count, f.previous)) ?? [1]));
  return (
    <div>
      <PeriodPicker value={period} onChange={setPeriod} />
      {report.isLoading ? (
        <Loading />
      ) : report.error ? (
        <Alert>{errorMessage(report.error)}</Alert>
      ) : (
        r && (
          <div className="flex flex-col gap-4">
            <Panel>
              <PanelHeader title="How far people get" description="Each try at booking online, by the furthest stage it reached. The lighter bar is the period before, same length." />
              <ul className="flex flex-col gap-3 px-5 py-4">
                {r.funnel.map((f) => (
                  <li key={f.key} className="grid grid-cols-[minmax(0,14rem)_1fr_auto] items-center gap-3 text-sm">
                    <span>{f.label}</span>
                    <span className="flex flex-col gap-1">
                      <span className="h-3 rounded-full bg-brand" style={{ width: `${(f.count / top) * 100}%`, minWidth: f.count ? "0.5rem" : 0 }} />
                      <span className="h-1.5 rounded-full bg-brand/30" style={{ width: `${(f.previous / top) * 100}%`, minWidth: f.previous ? "0.25rem" : 0 }} />
                    </span>
                    <span className="text-right tabular-nums">
                      <span className="font-semibold">{f.count}</span> <span className="text-muted">({f.previous})</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel>
              <PanelHeader title="Signed in, didn't book" description="We know who these are. Text only clients who've said yes to texts about offers." />
              {r.unfinished.length === 0 ? (
                <p className="px-5 py-4 text-sm text-muted">Nobody in this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table-base">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Who</th>
                        <th>Stopped at</th>
                        <th>Picked</th>
                        <th>Looking at</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {r.unfinished.map((u) => (
                        <tr key={u.id}>
                          <td className="whitespace-nowrap">{dayLabel(u.at.slice(0, 10), { month: "short", day: "numeric" })} {u.at.slice(11, 16)}</td>
                          <td>
                            {u.client ? (
                              <a href={`/clients/${u.client.id}`} className="font-semibold text-brand">
                                {u.client.name}
                              </a>
                            ) : (
                              <span className="text-muted">New (no record yet)</span>
                            )}
                            <div className="text-xs text-muted">{u.phone}</div>
                          </td>
                          <td>{u.stoppedAt}</td>
                          <td className="whitespace-nowrap">{u.start ? `${dayLabel(u.start.slice(0, 10), { weekday: "short", month: "short", day: "numeric" })} ${time12(u.start)}` : <span className="text-muted">No time yet</span>}</td>
                          <td>
                            {u.services || "—"}
                            {u.guests > 1 && <span className="text-muted"> · {u.guests} people</span>}
                            {u.context === "mobile" && <span className="text-muted"> · mobile</span>}
                          </td>
                          <td className="text-right">
                            {u.client?.canText ? (
                              <Button variant="quiet" onClick={() => setTexting(u)}>
                                Text them
                              </Button>
                            ) : (
                              u.client && <span className="text-xs text-muted">No OK to text offers</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        )
      )}
      {texting?.client && <FollowUpDialog row={texting} onClose={() => setTexting(null)} />}
    </div>
  );
}

function FollowUpDialog({ row, onClose }: { row: UnfinishedBookingsReport["unfinished"][number]; onClose: () => void }) {
  const first = row.client!.name.split(" ")[0];
  const [body, setBody] = useState(`Hi ${first}, it looks like you didn't finish booking${row.services ? ` ${row.services}` : ""}. Can we help? Just reply to this text.`);
  const send = useMutation({ mutationFn: () => post(`/api/clients/${row.client!.id}/messages`, { channel: "sms", body }), onSuccess: onClose });
  return (
    <Dialog
      title={`Text ${row.client!.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={send.isPending} disabled={!body.trim()} onClick={() => send.mutate()}>
            Send
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        <TextAreaField label="Message" rows={4} maxLength={1600} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
    </Dialog>
  );
}
